import { parse } from "vega";
import type { Mark, Spec } from "vega";
import { compile, type TopLevelSpec } from "vega-lite";
import { bindDataset } from "./dataBinding.js";
import { stripHostControlledKeys } from "./hostKeys.js";
import { findUnknownScales } from "./scaleRefs.js";
import { findUnknownVegaLiteMarks } from "./vegaLiteMarks.js";
import { findUnsafeProperties } from "./security.js";
import type {
  CustomChartGrammar,
  CustomChartSpec,
  JsonObject,
} from "./types.js";
import { VEGA_DATASET_NAME } from "./types.js";
import { validateVegaSchema } from "./vegaSchema.js";
import { isObject } from "./json.js";

export type CustomChartValidation =
  | {
      ok: true;
      /** The chart as it should be persisted: host-owned keys stripped. */
      chart: CustomChartSpec;
    }
  | { ok: false; errors: string[] };

function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

/** `table` is the host's dataset: the spec may shape it, never source it. */
function checkDatasetContract(spec: JsonObject): string[] {
  if (!Array.isArray(spec.data)) return [];
  return spec.data.flatMap((entry: unknown, index) =>
    isObject(entry) && entry.name === VEGA_DATASET_NAME && "source" in entry
      ? [
          `/data/${index}: the "${VEGA_DATASET_NAME}" data source is the Report's dataset — it cannot declare "source"`,
        ]
      : [],
  );
}

function checkVegaLite(spec: JsonObject): string[] {
  const marks = findUnknownVegaLiteMarks(spec);
  if (marks.length > 0) return marks;
  try {
    // Vega-Lite ships no runtime type guard; its compiler IS the validator.
    // A spec that compiles is renderable by definition.
    compile({ ...spec, data: { values: [] } } as unknown as TopLevelSpec);
    return [];
  } catch (error) {
    return [messageOf(error)];
  }
}

// The mark types Vega's scenegraph can draw. The schema types `mark.type` as
// any string and the parser defers the check to render time, where an unknown
// type fails with an unhelpful TypeError — so name it here instead.
const MARK_TYPES = [
  "arc",
  "area",
  "group",
  "image",
  "line",
  "path",
  "rect",
  "rule",
  "shape",
  "symbol",
  "text",
  "trail",
] as const satisfies readonly Mark["type"][];

const MARK_TYPE_SET: ReadonlySet<string> = new Set(MARK_TYPES);

function checkMarkTypes(marks: unknown, path: string): string[] {
  if (!Array.isArray(marks)) return [];
  return marks.flatMap((mark: unknown, index) => {
    if (!isObject(mark)) return [];
    const at = `${path}/${index}`;
    const own =
      typeof mark.type === "string" && !MARK_TYPE_SET.has(mark.type)
        ? [
            `${at}/type: unknown mark type "${mark.type}" (use one of ${MARK_TYPES.join(", ")})`,
          ]
        : [];
    return [...own, ...checkMarkTypes(mark.marks, `${at}/marks`)];
  });
}

function checkVega(spec: JsonObject): string[] {
  const contract = checkDatasetContract(spec);
  if (contract.length > 0) return contract;
  // Validate the spec as the renderer will see it: `table` holds values.
  const bound = bindDataset({ grammar: "vega", spec }, []);
  const schema = validateVegaSchema(bound);
  if (schema.length > 0) return schema;
  const marks = checkMarkTypes(spec.marks, "/marks");
  if (marks.length > 0) return marks;
  const scales = findUnknownScales(spec);
  if (scales.length > 0) return scales;
  try {
    // The schema proves shape; the parser proves meaning — expressions parse,
    // referenced data/signals exist, transforms are known. `ast: true` parses
    // expressions without generating code (CSP-safe), exactly as the renderer
    // runs them.
    parse(bound as Spec, undefined, { ast: true });
    return [];
  } catch (error) {
    return [messageOf(error)];
  }
}

const CHECKS: Record<CustomChartGrammar, (spec: JsonObject) => string[]> = {
  "vega-lite": checkVegaLite,
  vega: checkVega,
};

/**
 * The one gate every Custom chart spec passes before it is persisted or
 * rendered — pasted by an author, generated by the AI, or loaded from a saved
 * Report. In order: it must be a JSON object; host-owned keys are stripped;
 * nothing may reach outside the chart (remote data, images, links, external
 * bindings); then the grammar's own proof — Vega-Lite compiles, Vega passes
 * the official JSON schema and the Vega parser.
 */
export function validateCustomChart(input: {
  grammar: CustomChartGrammar;
  spec: unknown;
}): CustomChartValidation {
  if (!isObject(input.spec)) {
    return { ok: false, errors: ["The spec must be a JSON object"] };
  }
  const spec = stripHostControlledKeys(input.grammar, input.spec);
  const unsafe = findUnsafeProperties(spec);
  if (unsafe.length > 0) return { ok: false, errors: unsafe };
  const errors = CHECKS[input.grammar](spec);
  if (errors.length > 0) return { ok: false, errors };
  const chart: CustomChartSpec =
    input.grammar === "vega"
      ? { grammar: "vega", spec }
      : { grammar: "vega-lite", spec };
  return { ok: true, chart };
}
